import {
  ClearOutlined,
  DownOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  history,
  SelectLang as UmiSelectLang,
  useAccess,
  useModel,
} from '@umijs/max';
import {
  Alert,
  AutoComplete,
  Button,
  Checkbox,
  Descriptions,
  Dropdown,
  message,
  Modal,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { adminAuthFacade } from '@/facades/systemAdmin';
import {
  cacheScopeGroups,
  clearClientCache,
  DEFAULT_CACHE_SCOPES,
  type CacheScope,
  type CachePackageTenantRoute,
  lookupCachePackageTenant,
} from '@/facades/systemCache';
import {
  type AdminTenant,
  getAdminTenant,
  setAdminTenant,
} from '@/utils/adminTenant';
import {
  getAdminRegion,
  isAdminRegionConfigured,
  setAdminRegion,
} from '@/utils/adminRegion';

export type SiderTheme = 'light' | 'dark';

export const SelectLang: React.FC = () => {
  return (
    <UmiSelectLang
      style={{
        padding: 4,
      }}
    />
  );
};

export const Question: React.FC = () => {
  return (
    <a
      href="https://pro.ant.design/docs/getting-started"
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'inline-flex',
        padding: '4px',
        fontSize: '18px',
        color: 'inherit',
      }}
    >
      <QuestionCircleOutlined />
    </a>
  );
};

export const CurrentGroupBadge: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const groups = initialState?.currentUser?.groups || [];
  const names = groups.map((item) => item.name || item.code).filter(Boolean);
  const label = names.length ? names.join(' / ') : '未分组';

  return (
    <Tooltip title={`当前账号组：${label}`}>
      <Tag
        color={names.length ? 'blue' : 'default'}
        style={{
          maxWidth: 220,
          marginInlineEnd: 4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'default',
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
};

export const ProductSwitcher: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const currentCode = getAdminTenant();
  const current = tenants.find((item) => item.code === currentCode);

  useEffect(() => {
    if (!initialState?.currentUser) return;
    void adminAuthFacade
      .listTenants()
      .then(setTenants)
      .catch(() => undefined);
  }, [initialState?.currentUser]);

  if (!initialState?.currentUser || tenants.length <= 1) {
    return null;
  }

  return (
    <Dropdown
      menu={{
        items: tenants.map((tenant) => ({
          key: tenant.code,
          label: `${tenant.name}${tenant.code === currentCode ? '（当前）' : ''}`,
        })),
        onClick: ({ key }) => {
          if (key === currentCode) return;
          setAdminTenant(key);
          message.success('已切换产品');
          history.replace('/');
          window.location.reload();
        },
      }}
      trigger={['click']}
    >
      <Tag color="blue" style={{ cursor: 'pointer', marginInlineEnd: 4 }}>
        产品：{current?.name || currentCode || '选择产品'}
      </Tag>
    </Dropdown>
  );
};

export const AdminRegionSwitcher: React.FC = () => {
  const [region, setRegion] = useState(getAdminRegion);
  const label = region === 'us-east' ? '美东站' : '新加坡站';

  return (
    <Dropdown
      menu={{
        items: [
          { key: 'singapore', label: '新加坡站' },
          {
            key: 'us-east',
            label: '美东站',
            disabled: !isAdminRegionConfigured('us-east'),
          },
        ],
        onClick: ({ key }) => {
          const next = key as 'singapore' | 'us-east';
          if (next === region || !isAdminRegionConfigured(next)) return;
          setAdminRegion(next);
          setRegion(next);
          window.location.reload();
        },
      }}
      trigger={['click']}
    >
      <Button
        aria-label="切换管理区域"
        icon={<GlobalOutlined />}
        size="small"
        type="text"
        style={{ marginInlineEnd: 4 }}
      >
        区域：{label} <DownOutlined />
      </Button>
    </Dropdown>
  );
};

export const CurrentTenantCacheClearButton: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const access = useAccess();
  const [open, setOpen] = useState(false);
  const [bundleId, setBundleId] = useState('');
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [matches, setMatches] = useState<CachePackageTenantRoute[]>([]);
  const [scopes, setScopes] = useState<CacheScope[]>(DEFAULT_CACHE_SCOPES);
  const [querying, setQuerying] = useState(false);
  const [clearing, setClearing] = useState(false);
  const canClear = access.hasPermissionCodes?.('system.cache.clear:write');

  const queryBundle = async (value = bundleId) => {
    setQuerying(true);
    try {
      const response = await lookupCachePackageTenant({
        bundle_id: value,
        limit: 50,
      });
      setMatches(response.matches);
      const seen = new Set<string>();
      const sortedMatches = [...response.matches].sort((left, right) => {
        if (left.current_tenant_matched !== right.current_tenant_matched) {
          return left.current_tenant_matched ? -1 : 1;
        }
        return (
          left.bundle_id.localeCompare(right.bundle_id) ||
          left.platform.localeCompare(right.platform)
        );
      });
      setOptions(
        sortedMatches
          .filter((item) => {
            const key = item.bundle_id.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map((item) => ({
            value: item.bundle_id,
            label: item.bundle_id,
          })),
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : '查询包归属失败');
    } finally {
      setQuerying(false);
    }
  };

  const handleSearch = (value: string) => {
    setBundleId(value);
    if (value.trim().length >= 1) {
      void queryBundle(value);
    } else {
      void queryBundle('');
    }
  };

  useEffect(() => {
    if (open) {
      void queryBundle('');
    }
  }, [open]);

  const handleClear = async () => {
    if (scopes.length === 0) {
      message.warning('请至少选择一个清理范围');
      return;
    }
    setClearing(true);
    try {
      const result = await clearClientCache(scopes);
      if (result.scheduled) {
        message.success('当前产品缓存清理任务已提交');
      } else {
        message.success(`已清理当前产品缓存，删除 ${result.deleted_keys} 个键`);
      }
      setOpen(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '清理缓存失败');
    } finally {
      setClearing(false);
    }
  };

  const currentTenant = getAdminTenant() || 'foresee';
  const visibleMatches = useMemo(() => {
    const selectedBundleId = bundleId.trim().toLowerCase();
    const source = selectedBundleId
      ? matches.filter(
          (item) => item.bundle_id.toLowerCase() === selectedBundleId,
        )
      : matches.filter((item) => item.current_tenant_matched);
    return [...source]
      .sort((left, right) => {
        const leftRank = left.platform === 'android' ? 0 : 1;
        const rightRank = right.platform === 'android' ? 0 : 1;
        return (
          leftRank - rightRank ||
          left.platform.localeCompare(right.platform) ||
          left.bundle_id.localeCompare(right.bundle_id)
        );
      })
      .slice(0, 2);
  }, [bundleId, matches]);
  const hasExactBundle = matches.some(
    (item) => item.bundle_id.toLowerCase() === bundleId.trim().toLowerCase(),
  );

  if (!initialState?.currentUser || !canClear) {
    return null;
  }

  return (
    <>
      <Tooltip title="清除当前产品客户端缓存">
        <Button
          size="small"
          icon={<ClearOutlined />}
          onClick={() => setOpen(true)}
        >
          清缓存
        </Button>
      </Tooltip>
      <Modal
        title="清除当前产品缓存"
        open={open}
        confirmLoading={clearing}
        okText="确认清除"
        cancelText="取消"
        onOk={handleClear}
        onCancel={() => setOpen(false)}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="确认目标包在当前租户？"
            description="直接选择包名即可确认归属；也可以手动输入包名，输入时会自动补全。"
          />
          <Space.Compact style={{ width: '100%' }}>
            <AutoComplete
              allowClear
              value={bundleId}
              options={options}
              onSearch={handleSearch}
              onSelect={(value) => {
                setBundleId(value);
                void queryBundle(value);
              }}
              onChange={(value) => setBundleId(value)}
              placeholder="选择包名，也可手动输入"
              style={{ flex: 1 }}
            />
            <Button loading={querying} onClick={() => queryBundle()}>
              查询
            </Button>
          </Space.Compact>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="当前产品">
              {currentTenant}
            </Descriptions.Item>
            <Descriptions.Item label="清理范围">
              <Space direction="vertical" size={8}>
                {cacheScopeGroups.map((group) => (
                  <Space direction="vertical" size={4} key={group.title}>
                    <span>{group.title}</span>
                    <Checkbox.Group
                      options={group.options}
                      value={scopes}
                      onChange={(values) => {
                        const groupValues = new Set(group.options.map((option) => option.value));
                        const next = scopes.filter((scope) => !groupValues.has(scope));
                        setScopes([...next, ...(values as CacheScope[])]);
                      }}
                    />
                  </Space>
                ))}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="包归属">
              {visibleMatches.length ? (
                <Space direction="vertical" size={4}>
                  {visibleMatches.map((item) => (
                    <Space key={`${item.bundle_id}:${item.platform}`}>
                      <Tag color={item.current_tenant_matched ? 'green' : 'red'}>
                        {item.current_tenant_matched ? '匹配' : '不匹配'}
                      </Tag>
                      <span>
                        {item.bundle_id} / {item.platform} / {item.tenant_name}
                        （{item.tenant_code}）
                      </span>
                    </Space>
                  ))}
                </Space>
              ) : bundleId.trim() && !querying && !hasExactBundle ? (
                <span style={{ color: '#999' }}>未找到该包名归属</span>
              ) : (
                <span style={{ color: '#999' }}>请选择或输入包名</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Modal>
    </>
  );
};
